import {
  BedDouble,
  CalendarDays,
  Check,
  CircleAlert,
  Clock3,
  FileText,
  Hotel,
  LoaderCircle,
  Luggage,
  Plane,
  PlaneLanding,
  PlaneTakeoff,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FC, type KeyboardEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, num, str } from '@/app/lib/a2ui/props.ts';
import {
  advanceBookingDashboard,
  seedBookingDashboard,
  useBookingDashboard,
  type BookingItineraryDay,
  type BookingItinerarySlot,
} from '@/app/agent/booking-dashboard-store.ts';

const messages = defineMessages({
  fallbackTitle: {
    id: 'tripBooking.fallbackTitle',
    defaultMessage: 'Turn your plan into a trip',
    description: 'Fallback heading for the simulated booking journey',
  },
  fallbackSubtitle: {
    id: 'tripBooking.fallbackSubtitle',
    defaultMessage: 'We’ll prepare an illustrative flight ticket and stay, then leave the full trip ready to review.',
    description: 'Fallback subtitle for the automatic simulated booking journey',
  },

  demoBadge: {
    id: 'tripBooking.demoBadge',
    defaultMessage: 'Demo flow · no charge or reservation',
    description: 'Visible caveat badge for the simulated booking flow',
  },

  destination: {
    id: 'tripBooking.destination',
    defaultMessage: 'Destination',
    description: 'Label for the booking destination field',
  },
  plan: {
    id: 'tripBooking.plan',
    defaultMessage: 'Plan',
    description: 'Label for the selected plan field',
  },
  vacationType: {
    id: 'tripBooking.vacationType',
    defaultMessage: 'Trip shape',
    description: 'Label for the selected vacation type',
  },
  from: {
    id: 'tripBooking.from',
    defaultMessage: 'Flying from',
    description: 'Label for the departure field',
  },
  timing: {
    id: 'tripBooking.timing',
    defaultMessage: 'Timing',
    description: 'Label for the timing field',
  },
  duration: {
    id: 'tripBooking.duration',
    defaultMessage: '{days} days',
    description: 'Formatted booking duration',
  },
  budget: {
    id: 'tripBooking.budget',
    defaultMessage: 'Illustrative total',
    description: 'Label for the illustrative total field',
  },
  budgetMix: {
    id: 'tripBooking.budgetMix',
    defaultMessage: 'Budget mix',
    description: 'Label for the selected budget mix in trip details',
  },
  daysMix: {
    id: 'tripBooking.daysMix',
    defaultMessage: 'Itinerary shape',
    description: 'Label for the selected days-dependent itinerary shape',
  },
  style: {
    id: 'tripBooking.style',
    defaultMessage: 'Trip feel',
    description: 'Label for the style field in trip details',
  },
  visa: {
    id: 'tripBooking.visa',
    defaultMessage: 'Visa lane',
    description: 'Label for the visa field in trip details',
  },
  party: {
    id: 'tripBooking.party',
    defaultMessage: 'Travelling with',
    description: 'Label for the party field in trip details',
  },
  dining: {
    id: 'tripBooking.dining',
    defaultMessage: 'Food rhythm',
    description: 'Label for the dining field',
  },
  pace: {
    id: 'tripBooking.pace',
    defaultMessage: 'Daily pace',
    description: 'Label for the pace field',
  },
  status: {
    id: 'tripBooking.status',
    defaultMessage: 'Booking status',
    description: 'Heading for the staged booking status rail',
  },
  flightStage: {
    id: 'tripBooking.flightStage',
    defaultMessage: 'Flight ticket',
    description: 'Name of the first simulated booking stage',
  },
  hotelStage: {
    id: 'tripBooking.hotelStage',
    defaultMessage: 'Best-fit hotel',
    description: 'Name of the second simulated booking stage',
  },
  detailsStage: {
    id: 'tripBooking.detailsStage',
    defaultMessage: 'Trip details',
    description: 'Name of the third simulated booking stage',
  },

  notStarted: {
    id: 'tripBooking.notStarted',
    defaultMessage: 'Up next',
    description: 'Status for a booking stage not started yet',
  },
  selected: {
    id: 'tripBooking.selected',
    defaultMessage: 'Selected',
    description: 'Status for a selected simulated booking item',
  },

  route: {
    id: 'tripBooking.route',
    defaultMessage: '{origin} → {destination}',
    description: 'Illustrative flight route',
  },



  processingFlight: {
    id: 'tripBooking.processingFlight',
    defaultMessage: 'Preparing your illustrative flight ticket',
    description: 'Processing title while the simulated flight ticket is prepared',
  },
  processingHotel: {
    id: 'tripBooking.processingHotel',
    defaultMessage: 'Preparing your illustrative stay',
    description: 'Processing title while the simulated hotel result is prepared',
  },
  processingBody: {
    id: 'tripBooking.processingBody',
    defaultMessage: 'This takes a few seconds in the demo. Nothing is sent to a real provider.',
    description: 'Explanation shown during simulated booking processing',
  },

  hotelWaiting: {
    id: 'tripBooking.hotelWaiting',
    defaultMessage: 'The illustrative stay starts next.',
    description: 'Waiting state before automatic hotel processing starts',
  },
  flightTicketHeading: {
    id: 'tripBooking.flightTicketHeading',
    defaultMessage: 'Illustrative flight ticket',
    description: 'Heading for the simulated flight ticket result',
  },
  priceLabel: {
    id: 'tripBooking.priceLabel',
    defaultMessage: 'Price',
    description: 'Label for the illustrative flight price',
  },

  travellerFallback: {
    id: 'tripBooking.travellerFallback',
    defaultMessage: 'Traveller',
    description: 'Fallback traveller label for simulated booking results',
  },
  passengerProfile: {
    id: 'tripBooking.passengerProfile',
    defaultMessage: 'Passenger profile',
    description: 'Heading for the demo passenger details on the ticket',
  },
  firstName: {
    id: 'tripBooking.firstName',
    defaultMessage: 'Name',
    description: 'Label for the demo passenger first name',
  },
  surname: {
    id: 'tripBooking.surname',
    defaultMessage: 'Surname',
    description: 'Label for the demo passenger surname',
  },
  passport: {
    id: 'tripBooking.passport',
    defaultMessage: 'Passport',
    description: 'Label for the demo passport identifier',
  },
  gender: {
    id: 'tripBooking.gender',
    defaultMessage: 'Gender',
    description: 'Label for the demo passenger gender',
  },
  dateOfBirth: {
    id: 'tripBooking.dateOfBirth',
    defaultMessage: 'Date of birth',
    description: 'Label for the demo passenger date of birth',
  },
  age: {
    id: 'tripBooking.age',
    defaultMessage: 'Age',
    description: 'Label for the demo passenger age',
  },
  demoFirstName: {
    id: 'tripBooking.demoFirstName',
    defaultMessage: 'Alex',
    description: 'Illustrative first name shown on demo ticket and stay cards',
  },
  demoSurname: {
    id: 'tripBooking.demoSurname',
    defaultMessage: 'Morgan',
    description: 'Illustrative surname shown on demo ticket and stay cards',
  },
  demoPassport: {
    id: 'tripBooking.demoPassport',
    defaultMessage: 'P1234567',
    description: 'Illustrative passport identifier shown on demo ticket and stay cards',
  },
  demoGender: {
    id: 'tripBooking.demoGender',
    defaultMessage: 'X',
    description: 'Illustrative gender marker shown on demo ticket and stay cards',
  },
  demoDateOfBirth: {
    id: 'tripBooking.demoDateOfBirth',
    defaultMessage: '12 May 1990',
    description: 'Illustrative date of birth shown on demo ticket and stay cards',
  },
  demoAge: {
    id: 'tripBooking.demoAge',
    defaultMessage: '35',
    description: 'Illustrative age shown on demo ticket and stay cards',
  },
  male: {
    id: 'tripBooking.male',
    defaultMessage: 'Male',
    description: 'Display value for a male demo traveller selection',
  },
  female: {
    id: 'tripBooking.female',
    defaultMessage: 'Female',
    description: 'Display value for a female demo traveller selection',
  },
  flightNumber: {
    id: 'tripBooking.flightNumber',
    defaultMessage: 'WF 214',
    description: 'Illustrative flight number shown on the demo ticket',
  },
  airlineLabel: {
    id: 'tripBooking.airlineLabel',
    defaultMessage: 'Airline',
    description: 'Label for the illustrative airline name',
  },
  airlineName: {
    id: 'tripBooking.airlineName',
    defaultMessage: 'Wayfarer Air',
    description: 'Illustrative airline name shown on the demo ticket',
  },
  seatLabel: {
    id: 'tripBooking.seatLabel',
    defaultMessage: 'Seat',
    description: 'Label for the illustrative seat assignment',
  },
  seatNumber: {
    id: 'tripBooking.seatNumber',
    defaultMessage: '14A',
    description: 'Illustrative seat shown on the demo ticket',
  },
  flightCode: {
    id: 'tripBooking.flightCode',
    defaultMessage: 'Flight',
    description: 'Label for the illustrative flight number',
  },
  flightDate: {
    id: 'tripBooking.flightDate',
    defaultMessage: '14 October 2026',
    description: 'Illustrative flight date shown on the demo ticket',
  },
  departure: {
    id: 'tripBooking.departure',
    defaultMessage: 'Departure',
    description: 'Label for the illustrative departure details',
  },
  arrival: {
    id: 'tripBooking.arrival',
    defaultMessage: 'Arrival',
    description: 'Label for the illustrative arrival details',
  },
  terminal: {
    id: 'tripBooking.terminal',
    defaultMessage: 'Terminal',
    description: 'Label for an illustrative airport terminal',
  },
  departureTerminal: {
    id: 'tripBooking.departureTerminal',
    defaultMessage: 'Terminal 3',
    description: 'Illustrative departure terminal shown on the demo ticket',
  },
  arrivalTerminal: {
    id: 'tripBooking.arrivalTerminal',
    defaultMessage: 'Terminal 2',
    description: 'Illustrative arrival terminal shown on the demo ticket',
  },
  departureTime: {
    id: 'tripBooking.departureTime',
    defaultMessage: '09:10',
    description: 'Illustrative departure time in the simulated flight ticket',
  },
  arrivalTime: {
    id: 'tripBooking.arrivalTime',
    defaultMessage: '12:30',
    description: 'Illustrative arrival time in the simulated flight ticket',
  },
  hotelCost: {
    id: 'tripBooking.hotelCost',
    defaultMessage: 'Illustrative stay total',
    description: 'Label for the illustrative hotel cost',
  },
  tripTotal: {
    id: 'tripBooking.tripTotal',
    defaultMessage: 'Illustrative trip total',
    description: 'Label for the illustrative full trip cost',
  },
  nights: {
    id: 'tripBooking.nights',
    defaultMessage: '{nights} nights',
    description: 'Formatted illustrative hotel stay length',
  },
  stayWindow: {
    id: 'tripBooking.stayWindow',
    defaultMessage: 'Stay window',
    description: 'Label for the illustrative hotel stay duration',
  },

  checkInTime: {
    id: 'tripBooking.checkInTime',
    defaultMessage: '15:00',
    description: 'Illustrative hotel check-in time',
  },
  checkOutTime: {
    id: 'tripBooking.checkOutTime',
    defaultMessage: '11:00',
    description: 'Illustrative hotel check-out time',
  },

  hotelArea: {
    id: 'tripBooking.hotelArea',
    defaultMessage: 'Old Town',
    description: 'Illustrative hotel area used in the demo address',
  },
  airConditioning: {
    id: 'tripBooking.airConditioning',
    defaultMessage: 'Air conditioning',
    description: 'Illustrative room amenity',
  },
  roomNumberValue: {
    id: 'tripBooking.roomNumberValue',
    defaultMessage: '204',
    description: 'Illustrative room number shown in the hotel demo',
  },
  roomNumber: {
    id: 'tripBooking.roomNumber',
    defaultMessage: 'Room number',
    description: 'Label for the illustrative room number',
  },

  roomConditions: {
    id: 'tripBooking.roomConditions',
    defaultMessage: 'Room conditions',
    description: 'Label for illustrative room conditions',
  },

  seaView: {
    id: 'tripBooking.seaView',
    defaultMessage: 'Sea view',
    description: 'Illustrative room view for a higher budget',
  },
  cityView: {
    id: 'tripBooking.cityView',
    defaultMessage: 'City view',
    description: 'Illustrative room view for a lower budget',
  },
  bed: {
    id: 'tripBooking.bed',
    defaultMessage: 'Bed',
    description: 'Label for the illustrative bed arrangement',
  },
  singleBed: {
    id: 'tripBooking.singleBed',
    defaultMessage: '1 place',
    description: 'Illustrative single-bed arrangement for a solo traveller',
  },
  doubleBed: {
    id: 'tripBooking.doubleBed',
    defaultMessage: '2 places',
    description: 'Illustrative double-bed arrangement for multiple travellers',
  },
  roomStatus: {
    id: 'tripBooking.roomStatus',
    defaultMessage: 'Room status',
    description: 'Label for the illustrative room tier',
  },
  economyRoom: {
    id: 'tripBooking.economyRoom',
    defaultMessage: 'Economy',
    description: 'Illustrative room tier for a lower trip budget',
  },
  comfortRoom: {
    id: 'tripBooking.comfortRoom',
    defaultMessage: 'Comfort',
    description: 'Illustrative room tier for a mid-range trip budget',
  },
  luxuryRoom: {
    id: 'tripBooking.luxuryRoom',
    defaultMessage: 'Luxury',
    description: 'Illustrative room tier for a higher trip budget',
  },
  checkInLabel: {
    id: 'tripBooking.checkInLabel',
    defaultMessage: 'Check-in',
    description: 'Label for the hotel check-in time',
  },
  checkOutLabel: {
    id: 'tripBooking.checkOutLabel',
    defaultMessage: 'Check-out',
    description: 'Label for the hotel check-out time',
  },


  hotelHeading: {
    id: 'tripBooking.hotelHeading',
    defaultMessage: 'Match the stay to your plan',
    description: 'Heading for the second booking stage',
  },
  hotelBody: {
    id: 'tripBooking.hotelBody',
    defaultMessage: 'The recommendation follows the rhythm you chose, so the base supports the trip instead of fighting it. Availability is not connected.',
    description: 'Body for the second booking stage',
  },

  hotelCoastal: {
    id: 'tripBooking.hotelCoastal',
    defaultMessage: 'Walkable waterfront stay',
    description: 'Plan-driven illustrative stay for a coastal plan',
  },
  hotelUrban: {
    id: 'tripBooking.hotelUrban',
    defaultMessage: 'Central heritage stay',
    description: 'Plan-driven illustrative stay for an urban plan',
  },
  hotelNature: {
    id: 'tripBooking.hotelNature',
    defaultMessage: 'Scenic lodge or nature-edge inn',
    description: 'Plan-driven illustrative stay for a nature plan',
  },


  hotelChosen: {
    id: 'tripBooking.hotelChosen',
    defaultMessage: 'Illustrative stay ready',
    description: 'Status after the automatic illustrative stay is prepared',
  },
  detailsHeading: {
    id: 'tripBooking.detailsHeading',
    defaultMessage: 'Review the full trip',
    description: 'Heading for the final booking stage',
  },
  detailsBody: {
    id: 'tripBooking.detailsBody',
    defaultMessage: 'Here is the complete handoff: what you told us, the plan you chose and the day-by-day rhythm it creates.',
    description: 'Body for the final booking stage',
  },
  itineraryHeading: {
    id: 'tripBooking.itineraryHeading',
    defaultMessage: 'Illustrative itinerary',
    description: 'Heading for the day-by-day itinerary',
  },
  itineraryWindow: {
    id: 'tripBooking.itineraryWindow',
    defaultMessage: 'First 12 days shown in detail',
    description: 'Note explaining the itinerary detail cap',
  },
  day: {
    id: 'tripBooking.day',
    defaultMessage: 'Day {day}',
    description: 'Day label in the itinerary',
  },
  restOfTrip: {
    id: 'tripBooking.restOfTrip',
    defaultMessage: '{days} more days follow the same slower rhythm, with room for recovery, local errands and a return to the places you liked best.',
    description: 'Summary for days beyond the detailed itinerary cap',
  },
  readyHeading: {
    id: 'tripBooking.readyHeading',
    defaultMessage: 'Your trip plan is ready',
    description: 'Heading after the staged booking flow is complete',
  },
  readyBody: {
    id: 'tripBooking.readyBody',
    defaultMessage: 'The flight, stay and itinerary are lined up as a clear next step. Nothing has been booked or charged.',
    description: 'Body after the staged booking flow is complete',
  },

  emptyTitle: {
    id: 'tripBooking.emptyTitle',
    defaultMessage: 'Choose a plan first',
    description: 'Heading shown when booking is opened before a plan exists',
  },
  emptyBody: {
    id: 'tripBooking.emptyBody',
    defaultMessage: 'Pick a place, compare the three plans, and come back here for the staged trip handoff.',
    description: 'Body shown when booking is opened before a plan exists',
  },
});

export { messages as tripBookingMessages };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readItinerary(value: unknown): BookingItineraryDay[] {
  return arr(value)
    .flatMap((entry) => {
      if (!isRecord(entry)) {
        return [];
      }
      const day = num(entry.day);
      const slots: BookingItinerarySlot[] = arr(entry.slots).flatMap((slot) => {
        if (!isRecord(slot)) {
          return [];
        }
        const time = str(slot.time);
        const detail = str(slot.detail);
        return time && detail ? [{ time, detail }] : [];
      });
      const summary = str(entry.summary);
      return day && (slots.length > 0 || summary) ? [{ day, summary: summary || undefined, slots }] : [];
    })
    .sort((left, right) => left.day - right.day)
    .slice(0, 12);
}

function fallbackItinerary(
  days: number,
  city: string,
  destination: string,
  planType: string,
  dining: string,
  pace: string,
): BookingItineraryDay[] {
  const base = city || destination || 'your base';
  const sets: Record<string, [string, string, string, string]> = {
    coastal: [
      `Ease into ${base} with a waterfront walk`,
      'Follow the shore or harbour for the day’s signature stop',
      `Take a casual meal: ${dining || 'a local café near the water'}`,
      'Leave the afternoon open for a swim, market or slow nap',
    ],
    urban: [
      `Start with a neighbourhood walk through ${base}`,
      'Spend the late morning at a heritage place, museum or market',
      `Eat locally: ${dining || 'a neighbourhood restaurant'}`,
      'Add a small cultural stop, spa pause or evening food walk',
    ],
    nature: [
      `Set out early for a viewpoint near ${base}`,
      'Take the day’s marked trail or scenic anchor at an easy pace',
      `Refuel simply: ${dining || 'a picnic or trail-side café'}`,
      `Keep the afternoon for a second view, wildlife stop or recovery · ${pace || 'unhurried rhythm'}`,
    ],
  };
  const chosen = sets[planType] ?? sets.urban;
  return Array.from({ length: Math.min(Math.max(1, Math.round(days)), 12) }, (_, index) => ({
    day: index + 1,
    slots: [
      { time: '08:00', detail: index === 0 ? chosen[0] : `Begin day ${index + 1} with a local coffee and an easy start in ${base}` },
      { time: '10:00', detail: index % 3 === 0 ? chosen[1] : `Make space for a new neighbourhood, view or local anchor around ${base}` },
      { time: '13:00', detail: chosen[2] },
      { time: '16:30', detail: index % 2 === 0 ? chosen[3] : `Keep the later hours flexible for a market, gallery, beach or trail-side pause` },
      { time: '19:30', detail: `Close the day with ${dining || 'a relaxed local dinner'} and a short walk back to base` },
    ],
  }));
}

function hotelLabelFor(planType: string, intl: ReturnType<typeof useIntl>): string {
  if (planType === 'coastal') {
    return intl.formatMessage(messages.hotelCoastal);
  }
  if (planType === 'nature') {
    return intl.formatMessage(messages.hotelNature);
  }
  return intl.formatMessage(messages.hotelUrban);
}

function ageForDateOfBirth(value: string, fallback: string): string {
  const parts = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  const parsed = parts
    ? (() => {
        let month = Number(parts[1]);
        let day = Number(parts[2]);
        if (month > 12 && day <= 12) {
          [month, day] = [day, month];
        }
        return new Date(Number(parts[3]), month - 1, day);
      })()
    : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return fallback;
  }
  const today = new Date();
  let age = today.getFullYear() - parsed.getFullYear();
  const birthdayHasPassed = today.getMonth() > parsed.getMonth()
    || (today.getMonth() === parsed.getMonth() && today.getDate() >= parsed.getDate());
  if (!birthdayHasPassed) {
    age -= 1;
  }
  return age >= 0 && age < 130 ? String(age) : fallback;
}

type BookingProcessState = 'idle' | 'processing' | 'complete';

const BOOKING_PROCESSING_MS = 3000;
const BOOKING_RESULT_HOLD_MS = 3000;

export const TripBooking: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const title = str(node.props.title) || intl.formatMessage(messages.fallbackTitle);
  const subtitle = str(node.props.subtitle) || intl.formatMessage(messages.fallbackSubtitle);
  const destinationName = str(node.props.destinationName);
  const destinationCountry = str(node.props.destinationCountry);
  const city = str(node.props.city);
  const planId = str(node.props.planId);
  const planName = str(node.props.planName);
  const planType = str(node.props.planType);
  const budgetMix = str(node.props.budgetMix);
  const daysMix = str(node.props.daysMix);
  const dining = str(node.props.dining);
  const pace = str(node.props.pace);
  const origin = str(node.props.origin);
  const season = str(node.props.season);
  const visa = str(node.props.visa);
  const style = str(node.props.style);
  const party = str(node.props.party);
  const firstName = str(node.props.firstName);
  const surname = str(node.props.surname);
  const passportNumber = str(node.props.passportNumber);
  const dateOfBirth = str(node.props.dateOfBirth);
  const gender = str(node.props.gender);
  const days = num(node.props.days) ?? null;
  const budget = num(node.props.budget) ?? null;
  const estimatedCost = num(node.props.estimatedCost) ?? null;
  const destination = [destinationName, destinationCountry].filter(Boolean).join(', ');
  const hasTrip = Boolean(planName);
  const tripKey = [destinationName, city, planId || planName, planType, origin].join('|');
  const itinerary = useMemo(() => {
    const supplied = readItinerary(node.props.itinerary);
    const fallback = fallbackItinerary(days ?? 7, city, destinationName, planType, dining, pace);
    if (supplied.length === 0) {
      return fallback;
    }
    const fallbackByDay = new Map(fallback.map((item) => [item.day, item]));
    return supplied.map((item) => ({
      ...item,
      slots: item.slots.length > 0 ? item.slots : fallbackByDay.get(item.day)?.slots ?? [],
    }));
  }, [city, days, destinationName, dining, node.props.itinerary, pace, planType]);
  const remainingDays = Math.max(0, (days ?? itinerary.length) - itinerary.length);
  const dashboard = useBookingDashboard();
  const stage = hasTrip ? dashboard?.tripKey === tripKey ? dashboard.stage : 0 : 0;
  const [activeStage, setActiveStage] = useState<0 | 1 | 2>(0);
  const [flightProcess, setFlightProcess] = useState<BookingProcessState>(hasTrip ? 'processing' : 'idle');
  const [hotelProcess, setHotelProcess] = useState<BookingProcessState>('idle');
  const flightTimerRef = useRef<number | null>(null);
  const hotelHandoffTimerRef = useRef<number | null>(null);
  const hotelTimerRef = useRef<number | null>(null);
  const flightLabel = intl.formatMessage(messages.route, {
    origin: origin || 'your departure city',
    destination: city || destinationName || 'your destination',
  });
  const hotelLabel = hotelLabelFor(planType, intl);

  useEffect(() => {
    if (!hasTrip || dashboard?.tripKey !== tripKey) {
      setActiveStage(0);
      return;
    }
    if (stage >= 3) {
      setActiveStage(2);
    }
  }, [dashboard?.tripKey, hasTrip, stage, tripKey]);

  useEffect(() => {
    const clearBookingTimers = (): void => {
      if (flightTimerRef.current !== null) {
        window.clearTimeout(flightTimerRef.current);
        flightTimerRef.current = null;
      }
      if (hotelHandoffTimerRef.current !== null) {
        window.clearTimeout(hotelHandoffTimerRef.current);
        hotelHandoffTimerRef.current = null;
      }
      if (hotelTimerRef.current !== null) {
        window.clearTimeout(hotelTimerRef.current);
        hotelTimerRef.current = null;
      }
    };

    if (!hasTrip) {
      clearBookingTimers();
      setActiveStage(0);
      setFlightProcess('idle');
      setHotelProcess('idle');
      return clearBookingTimers;
    }

    clearBookingTimers();

    const initialStage = dashboard?.tripKey === tripKey ? dashboard.stage : 0;
    const startHotelSequence = (): void => {
      setActiveStage(1);
      setHotelProcess('processing');
      hotelTimerRef.current = window.setTimeout(() => {
        advanceBookingDashboard(2, { hotel: hotelLabel });
        setHotelProcess('complete');
      }, BOOKING_PROCESSING_MS);
    };

    if (initialStage >= 2) {
      setFlightProcess('complete');
      setHotelProcess('complete');
      setActiveStage(initialStage >= 3 ? 2 : 1);
      return clearBookingTimers;
    }

    if (initialStage >= 1) {
      setFlightProcess('complete');
      startHotelSequence();
      return clearBookingTimers;
    }

    setActiveStage(0);
    setFlightProcess('processing');
    setHotelProcess('idle');
    flightTimerRef.current = window.setTimeout(() => {
      advanceBookingDashboard(1, { flight: flightLabel });
      setFlightProcess('complete');
      hotelHandoffTimerRef.current = window.setTimeout(startHotelSequence, BOOKING_RESULT_HOLD_MS);
    }, BOOKING_PROCESSING_MS);

    return clearBookingTimers;
  }, [hasTrip, tripKey]);

  useEffect(() => {
    if (!hasTrip) {
      return;
    }
    seedBookingDashboard({
      tripKey,
      destinationName,
      destinationCountry,
      city,
      planId,
      planName,
      planType,
      origin,
      season,
      visa,
      style,
      party,
      days,
      budget,
      estimatedCost,
      dining,
      pace,
      budgetMix,
      daysMix,
      itinerary,
      remainingDays,
      flightLabel,
      hotelLabel,
      stage: dashboard?.tripKey === tripKey ? dashboard.stage : 0,
    });
  }, [
    budget,
    city,
    days,
    daysMix,
    destinationCountry,
    destinationName,
    dining,
    estimatedCost,
    flightLabel,
    hasTrip,
    hotelLabel,
    itinerary,
    origin,
    pace,
    party,
    planId,
    planName,
    planType,
    remainingDays,
    season,
    style,
    tripKey,
    visa,
    dashboard?.stage,
    dashboard?.tripKey,
  ]);

  const money = (value: number): string =>
    intl.formatNumber(value, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
  const demoDateOfBirth = dateOfBirth || intl.formatMessage(messages.demoDateOfBirth);
  const normalizedGender = gender.toLowerCase();
  const demoPassenger = {
    firstName: firstName || intl.formatMessage(messages.demoFirstName),
    surname: surname || intl.formatMessage(messages.demoSurname),
    passport: passportNumber || intl.formatMessage(messages.demoPassport),
    gender: normalizedGender === 'male'
      ? intl.formatMessage(messages.male)
      : normalizedGender === 'female'
        ? intl.formatMessage(messages.female)
        : gender || intl.formatMessage(messages.demoGender),
    dateOfBirth: demoDateOfBirth,
    age: ageForDateOfBirth(demoDateOfBirth, intl.formatMessage(messages.demoAge)),
  };
  const illustrativeTotal = estimatedCost ?? budget ?? 1500;
  const illustrativeFlightFare = Math.round(illustrativeTotal * 0.36);
  const illustrativeHotelCost = Math.max(0, illustrativeTotal - illustrativeFlightFare);
  const illustrativeNights = Math.max(1, (days ?? 7) - 1);
  const hotelAddressValue = [
    intl.formatMessage(messages.hotelArea),
    city || destinationName || 'Lagos',
    destinationCountry,
  ].filter(Boolean).join(' · ');
  const roomConditionsValue = [
    intl.formatMessage(messages.airConditioning),
    illustrativeTotal >= 1800 ? intl.formatMessage(messages.seaView) : intl.formatMessage(messages.cityView),
  ].join(' · ');
  const roomStatusValue = illustrativeTotal >= 2200
    ? intl.formatMessage(messages.luxuryRoom)
    : illustrativeTotal >= 1600
      ? intl.formatMessage(messages.comfortRoom)
      : intl.formatMessage(messages.economyRoom);
  const bedValue = !party || /solo|single|\b1\b/i.test(party)
    ? intl.formatMessage(messages.singleBed)
    : intl.formatMessage(messages.doubleBed);
  const departureLocation = origin || 'London';
  const arrivalLocation = city || destinationName || 'your destination';
  const flightSchedule = (
    <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-label={intl.formatMessage(messages.flightTicketHeading)}>
      <div className="min-w-0 rounded-[15px] border-2 border-primary/25 bg-primary/5 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="mt-2 break-words text-base font-bold text-foreground">{intl.formatMessage(messages.flightTicketHeading)}</p>
          </div>
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
            <CalendarDays className="h-4 w-4" />
          </span>
        </div>
        <p className="mt-1 break-words text-[15px] font-bold text-foreground">{flightLabel}</p>
        <p className="mt-3 text-[13px] font-semibold text-foreground" translate="no">{intl.formatMessage(messages.flightDate)}</p>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.priceLabel)}</span>
          <p className="text-2xl font-black leading-none text-foreground" translate="no">{money(illustrativeFlightFare)}</p>
        </div>
      </div>
      <div className="min-w-0 rounded-[15px] border border-border bg-card p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.departure)}</p>
            <p className="mt-1 break-words text-[15px] font-bold text-foreground">{departureLocation}</p>
          </div>
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
            <PlaneTakeoff className="h-4 w-4" />
          </span>
        </div>
        <p className="mt-3 text-2xl font-black leading-none text-foreground" translate="no">{intl.formatMessage(messages.departureTime)}</p>
        <p className="mt-3 text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.terminal)}</p>
        <p className="mt-1 text-[15px] font-bold text-foreground" translate="no">{intl.formatMessage(messages.departureTerminal)}</p>
      </div>
      <div className="min-w-0 rounded-[15px] border border-border bg-card p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.arrival)}</p>
            <p className="mt-1 break-words text-[15px] font-bold text-foreground">{arrivalLocation}</p>
          </div>
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
            <PlaneLanding className="h-4 w-4" />
          </span>
        </div>
        <p className="mt-3 text-2xl font-black leading-none text-foreground" translate="no">{intl.formatMessage(messages.arrivalTime)}</p>
        <p className="mt-3 text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.terminal)}</p>
        <p className="mt-1 text-[15px] font-bold text-foreground" translate="no">{intl.formatMessage(messages.arrivalTerminal)}</p>
      </div>
      <div className="min-w-0 rounded-[15px] border border-border bg-card p-4">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.airlineLabel)}</p>
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
            <Luggage className="h-4 w-4" />
          </span>
        </div>
        <p className="mt-2 break-words text-[15px] font-bold text-foreground">{intl.formatMessage(messages.airlineName)}</p>
        <p className="mt-3 text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.seatLabel)}</p>
        <p className="mt-1 text-[15px] font-bold text-foreground" translate="no">{intl.formatMessage(messages.seatNumber)}</p>
        <p className="mt-3 text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.flightCode)}</p>
        <p className="mt-1 text-[15px] font-bold text-foreground" translate="no">{intl.formatMessage(messages.flightNumber)}</p>
      </div>
    </div>
  );

  const stages = [
    { icon: Plane, label: intl.formatMessage(messages.flightStage) },
    { icon: Hotel, label: intl.formatMessage(messages.hotelStage) },
    { icon: FileText, label: intl.formatMessage(messages.detailsStage) },
  ];

  const handleStageKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
      return;
    }
    event.preventDefault();
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? 2
        : (index + (event.key === 'ArrowRight' ? 1 : -1) + stages.length) % stages.length;
    const nextStage = nextIndex as 0 | 1 | 2;
    setActiveStage(nextStage);
    requestAnimationFrame(() => document.getElementById(`trip-booking-tab-${nextStage}`)?.focus());
  };


  return (
    <div
      className="trip-booking-step flex w-full flex-col gap-6 text-foreground"
      data-wayfarer-step="5"
      data-wayfarer-step-available-through={hasTrip ? 5 : 4}
    >
      <header className="animate-fadeUp motion-reduce:animate-none">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="max-w-[44rem] text-balance text-3xl font-bold leading-tight tracking-tighter text-foreground sm:text-4xl">
              {title}
            </h1>
            <p className="mt-3 max-w-[44rem] text-base leading-relaxed text-muted-foreground">{subtitle}</p>
          </div>
          <div className="inline-flex w-fit shrink-0 items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-3 py-2 text-[13px] font-semibold text-primary">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            <span>{intl.formatMessage(messages.demoBadge)}</span>
          </div>
        </div>
      </header>

      {hasTrip ? (
        <>

          <section aria-labelledby="trip-booking-status">
            <h2 id="trip-booking-status" className="text-lg font-bold tracking-tight text-foreground">
              {intl.formatMessage(messages.status)}
            </h2>
            <div className="mt-3 grid gap-2 md:grid-cols-3" role="tablist" aria-labelledby="trip-booking-status">
              {stages.map(({ icon: Icon, label }, index) => {
                const tabIndex = index as 0 | 1 | 2;
                const complete = stage > index;
                const current = activeStage === tabIndex;
                return (
                  <button
                    key={label}
                    id={`trip-booking-tab-${tabIndex}`}
                    type="button"
                    role="tab"
                    aria-selected={current}
                    aria-controls={`trip-booking-panel-${tabIndex}`}
                    tabIndex={current ? 0 : -1}
                    onClick={() => setActiveStage(tabIndex)}
                    onKeyDown={(event) => handleStageKeyDown(event, index)}
                    className={`flex min-w-0 touch-manipulation items-center gap-3 rounded-[15px] border p-3.5 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${current ? 'border-primary/60 bg-card' : complete ? 'border-primary/35 bg-primary/10 hover:border-primary/60' : 'border-border bg-card/60 hover:border-primary/45'}`}
                  >
                    <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${complete ? 'bg-primary text-primary-foreground' : current ? 'bg-primary/12 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      {complete ? <Check className="h-4 w-4" aria-hidden="true" /> : <Icon className="h-4 w-4" aria-hidden="true" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[15px] font-bold text-foreground">{label}</span>
                      <span className="mt-0.5 block text-[13px] text-muted-foreground">
                        {complete ? intl.formatMessage(messages.selected) : current ? intl.formatMessage(messages.status) : intl.formatMessage(messages.notStarted)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          {activeStage === 0 ? (
            <section id="trip-booking-panel-0" role="tabpanel" aria-labelledby="trip-booking-tab-0" className="rounded-[15px] border border-border bg-card p-5">
              <div className="rounded-[15px] border border-border bg-background/70 p-4 sm:p-6">
                {flightSchedule}

                {flightProcess === 'processing' ? (
                    <div className="flex min-h-56 flex-col items-center justify-center text-center" aria-live="polite">
                      <LoaderCircle className="h-8 w-8 animate-spin text-primary motion-reduce:animate-none" aria-hidden="true" />
                      <p className="mt-4 text-base font-bold text-foreground">{intl.formatMessage(messages.processingFlight)}</p>
                      <p className="mt-2 max-w-xs text-[15px] leading-relaxed text-muted-foreground">{intl.formatMessage(messages.processingBody)}</p>

                    </div>
                  ) : flightProcess === 'complete' ? (
                    <div aria-live="polite">

                      <div className="mt-5 flex items-center gap-2">
                        <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.passengerProfile)}</p>
                        <p className="text-xs font-semibold text-muted-foreground">{party || intl.formatMessage(messages.travellerFallback)}</p>
                      </div>
                      <div className="mt-2 grid gap-x-4 gap-y-4 rounded-[15px] border border-border bg-card p-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-[repeat(6,minmax(0,1fr))]">
                        {[
                          [intl.formatMessage(messages.firstName), demoPassenger.firstName],
                          [intl.formatMessage(messages.surname), demoPassenger.surname],
                          [intl.formatMessage(messages.passport), demoPassenger.passport],
                          [intl.formatMessage(messages.gender), demoPassenger.gender],
                          [intl.formatMessage(messages.dateOfBirth), demoPassenger.dateOfBirth],
                          [intl.formatMessage(messages.age), demoPassenger.age],
                        ].map(([label, value]) => (
                          <div key={label} className="min-w-0">
                            <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{label}</p>
                            <p className="mt-1 break-words text-[15px] font-semibold text-foreground" translate="no">{value}</p>
                          </div>
                        ))}
                      </div>

                    </div>
                  ) : (
                    <div className="flex min-h-56 flex-col items-center justify-center text-center" aria-live="polite">
                      <LoaderCircle className="h-8 w-8 animate-spin text-primary motion-reduce:animate-none" aria-hidden="true" />
                      <p className="mt-4 text-base font-bold text-foreground">{intl.formatMessage(messages.processingFlight)}</p>
                      <p className="mt-2 max-w-xs text-[15px] leading-relaxed text-muted-foreground">{intl.formatMessage(messages.processingBody)}</p>
                    </div>
                  )}
              </div>
            </section>
          ) : activeStage === 1 ? (
            <section id="trip-booking-panel-1" role="tabpanel" aria-labelledby="trip-booking-tab-1" className="rounded-[15px] border border-border bg-card p-5">
              <div className="w-full">
                <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-2.5 py-1 text-[13px] font-bold uppercase tracking-caps text-primary">
                  <BedDouble className="h-3.5 w-3.5" aria-hidden="true" />
                  {intl.formatMessage(messages.hotelStage)}
                </div>
                <h2 id="trip-booking-hotel-heading" className="mt-3 text-2xl font-bold tracking-tight text-foreground">{intl.formatMessage(messages.hotelHeading)}</h2>
                <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{intl.formatMessage(messages.hotelBody)}</p>
              </div>
              <div className="mt-5 rounded-[15px] border border-border bg-background/70 p-5">
                {hotelProcess === 'processing' ? (
                  <div className="flex min-h-48 flex-col items-center justify-center text-center" aria-live="polite">
                    <LoaderCircle className="h-8 w-8 animate-spin text-primary motion-reduce:animate-none" aria-hidden="true" />
                    <p className="mt-4 text-base font-bold text-foreground">{intl.formatMessage(messages.processingHotel)}</p>
                    <p className="mt-2 max-w-xs text-[15px] leading-relaxed text-muted-foreground">{intl.formatMessage(messages.processingBody)}</p>
                  </div>
                ) : hotelProcess === 'complete' ? (
                  <div aria-live="polite">
                    <h3 className="flex items-center gap-3 text-lg font-bold text-foreground">
                      <span>{hotelLabel}</span>
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
                        <Hotel className="h-4 w-4" />
                      </span>
                    </h3>
                    <div className="mt-5 grid gap-x-6 gap-y-4 rounded-[15px] border border-border bg-card p-4 sm:grid-cols-2 lg:grid-cols-3">
                      <div className="min-w-0">
                        <p className="break-words text-[15px] font-semibold text-foreground">{hotelAddressValue}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.roomNumber)}</p>
                        <p className="mt-1 text-[15px] font-semibold text-foreground" translate="no">{intl.formatMessage(messages.roomNumberValue)}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.roomConditions)}</p>
                        <p className="mt-1 break-words text-[15px] font-semibold text-foreground">{roomConditionsValue}</p>
                      </div>
                    </div>
                    <div className="mt-5 grid gap-x-6 gap-y-4 rounded-[15px] border border-border bg-card p-4 sm:grid-cols-2 lg:grid-cols-4">
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.bed)}</p>
                        <p className="mt-1 break-words text-[15px] font-semibold text-foreground">{bedValue}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.roomStatus)}</p>
                        <p className="mt-1 break-words text-[15px] font-semibold text-foreground">{roomStatusValue}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.checkInLabel)}</p>
                        <p className="mt-1 text-[15px] font-semibold text-foreground" translate="no">{intl.formatMessage(messages.checkInTime)}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.checkOutLabel)}</p>
                        <p className="mt-1 text-[15px] font-semibold text-foreground" translate="no">{intl.formatMessage(messages.checkOutTime)}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.stayWindow)}</p>
                        <p className="mt-1 text-[15px] font-semibold text-foreground">{days !== null ? intl.formatMessage(messages.duration, { days }) : intl.formatMessage(messages.timing)}</p>
                        <p className="mt-1 text-[15px] text-muted-foreground">{intl.formatMessage(messages.nights, { nights: illustrativeNights })}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.hotelCost)}</p>
                        <p className="mt-1 text-[15px] font-bold text-foreground" translate="no">{money(illustrativeHotelCost)}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.tripTotal)}</p>
                        <p className="mt-1 text-[15px] font-bold text-foreground" translate="no">{money(illustrativeTotal)}</p>
                      </div>
                    </div>
                    <div className="mt-5 flex items-center gap-2">
                      <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.passengerProfile)}</p>
                      <p className="text-xs font-semibold text-muted-foreground">{party || intl.formatMessage(messages.travellerFallback)}</p>
                    </div>
                    <div className="mt-2 grid gap-x-4 gap-y-4 rounded-[15px] border border-border bg-card p-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-[repeat(6,minmax(0,1fr))]">
                      {[
                        [intl.formatMessage(messages.firstName), demoPassenger.firstName],
                        [intl.formatMessage(messages.surname), demoPassenger.surname],
                        [intl.formatMessage(messages.passport), demoPassenger.passport],
                        [intl.formatMessage(messages.gender), demoPassenger.gender],
                        [intl.formatMessage(messages.dateOfBirth), demoPassenger.dateOfBirth],
                        [intl.formatMessage(messages.age), demoPassenger.age],
                      ].map(([label, value]) => (
                        <div key={label} className="min-w-0">
                          <p className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{label}</p>
                          <p className="mt-1 break-words text-[15px] font-semibold text-foreground" translate="no">{value}</p>
                        </div>
                      ))}
                    </div>

                  </div>
                ) : (
                  <div className="flex min-h-48 flex-col items-center justify-center text-center" aria-live="polite">
                    <LoaderCircle className="h-8 w-8 animate-spin text-primary motion-reduce:animate-none" aria-hidden="true" />
                    <p className="mt-4 text-base font-bold text-foreground">{intl.formatMessage(messages.hotelWaiting)}</p>
                    <p className="mt-2 max-w-xs text-[15px] leading-relaxed text-muted-foreground">{intl.formatMessage(messages.processingBody)}</p>
                  </div>
                )}
              </div>
            </section>
          ) : stage < 3 ? (
            <section id="trip-booking-panel-2" role="tabpanel" aria-labelledby="trip-booking-tab-2" className="rounded-[15px] border border-border bg-card p-5">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div className="max-w-[42rem]">
                  <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-2.5 py-1 text-[13px] font-bold uppercase tracking-caps text-primary">
                    <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                    {intl.formatMessage(messages.detailsStage)}
                  </div>
                  <h2 id="trip-booking-details-heading" className="mt-3 text-2xl font-bold tracking-tight text-foreground">{intl.formatMessage(messages.detailsHeading)}</h2>
                  <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{intl.formatMessage(messages.detailsBody)}</p>
                </div>
                <div className="rounded-[15px] border border-primary/25 bg-primary/10 px-4 py-3 text-[15px] text-foreground">
                  <p className="font-bold">{intl.formatMessage(stage >= 2 ? messages.hotelChosen : messages.notStarted)}</p>
                  <p className="mt-1 text-muted-foreground">{hotelLabel}</p>
                </div>
              </div>
              <TripDetailGrid
                intl={intl}
                origin={origin}
                destination={destination}
                planName={planName}
                planType={planType}
                season={season}
                visa={visa}
                style={style}
                party={party}
                dining={dining}
                pace={pace}
                days={days}
                budget={budget}
                estimatedCost={estimatedCost}
                budgetMix={budgetMix}
                daysMix={daysMix}
                money={money}
              />
              <Itinerary
                intl={intl}
                itinerary={itinerary}
                remainingDays={remainingDays}
              />

            </section>
          ) : (
            <section id="trip-booking-panel-2" role="tabpanel" aria-labelledby="trip-booking-tab-2" className="rounded-[15px] border border-primary/35 bg-primary/5 p-5">
              <div className="flex items-start gap-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground"><Sparkles className="h-5 w-5" aria-hidden="true" /></span>
                <div>
                  <h2 id="trip-booking-ready-heading" className="text-2xl font-bold tracking-tight text-foreground">{intl.formatMessage(messages.readyHeading)}</h2>
                  <p className="mt-2 max-w-[42rem] text-[15px] leading-relaxed text-muted-foreground">{intl.formatMessage(messages.readyBody)}</p>
                </div>
              </div>
              <TripDetailGrid
                intl={intl}
                origin={origin}
                destination={destination}
                planName={planName}
                planType={planType}
                season={season}
                visa={visa}
                style={style}
                party={party}
                dining={dining}
                pace={pace}
                days={days}
                budget={budget}
                estimatedCost={estimatedCost}
                budgetMix={budgetMix}
                daysMix={daysMix}
                money={money}
              />
              <Itinerary intl={intl} itinerary={itinerary} remainingDays={remainingDays} />
            </section>
          )}

        </>
      ) : (
        <section className="rounded-[15px] border border-border bg-card p-6 sm:p-8" aria-labelledby="booking-empty-heading">
          <div className="flex items-start gap-4">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-primary/10 text-primary"><CircleAlert className="h-5 w-5" aria-hidden="true" /></span>
            <div>
              <h2 id="booking-empty-heading" className="text-xl font-bold tracking-tight text-foreground">{intl.formatMessage(messages.emptyTitle)}</h2>
              <p className="mt-2 max-w-[42rem] text-base leading-relaxed text-muted-foreground">{intl.formatMessage(messages.emptyBody)}</p>
            </div>
          </div>

        </section>
      )}
    </div>
  );
};

type TripDetailGridProps = {
  intl: ReturnType<typeof useIntl>;
  origin: string;
  destination: string;
  planName: string;
  planType: string;
  season: string;
  visa: string;
  style: string;
  party: string;
  dining: string;
  pace: string;
  days: number | null;
  budget: number | null;
  estimatedCost: number | null;
  budgetMix: string;
  daysMix: string;
  money: (value: number) => string;
};

const TripDetailGrid: FC<TripDetailGridProps> = ({
  intl,
  origin,
  destination,
  planName,
  planType,
  season,
  visa,
  style,
  party,
  dining,
  pace,
  days,
  budget,
  estimatedCost,
  budgetMix,
  daysMix,
  money,
}) => {
  const details = [
    [intl.formatMessage(messages.from), origin],
    [intl.formatMessage(messages.destination), destination],
    [intl.formatMessage(messages.plan), planName],
    [intl.formatMessage(messages.vacationType), planType],
    [intl.formatMessage(messages.timing), [season, days !== null ? intl.formatMessage(messages.duration, { days }) : ''].filter(Boolean).join(' · ')],
    [intl.formatMessage(messages.style), style],
    [intl.formatMessage(messages.visa), visa],
    [intl.formatMessage(messages.party), party],
    [intl.formatMessage(messages.dining), dining],
    [intl.formatMessage(messages.pace), pace],
    [intl.formatMessage(messages.budget), estimatedCost !== null ? money(estimatedCost) : budget !== null ? money(budget) : ''],
    [intl.formatMessage(messages.budgetMix), budgetMix],
    [intl.formatMessage(messages.daysMix), daysMix],
  ].filter(([, value]) => Boolean(value));
  return (
    <dl className="mt-6 grid gap-x-8 sm:grid-cols-2">
      {details.map(([label, value]) => (
        <div key={label} className="border-t border-border py-2.5">
          <dt className="text-[13px] font-semibold uppercase tracking-caps text-muted-foreground">{label}</dt>
          <dd className="mt-0.5 break-words text-[15px] font-semibold text-foreground">{value}</dd>
        </div>
      ))}
    </dl>
  );
};

type ItineraryProps = {
  intl: ReturnType<typeof useIntl>;
  itinerary: BookingItineraryDay[];
  remainingDays: number;
};

const Itinerary: FC<ItineraryProps> = ({ intl, itinerary, remainingDays }) => (
  <section className="mt-6" aria-labelledby="trip-booking-itinerary-heading">
    <div className="flex flex-wrap items-end justify-between gap-2">
      <div>
        <h3 id="trip-booking-itinerary-heading" className="text-lg font-bold tracking-tight text-foreground">{intl.formatMessage(messages.itineraryHeading)}</h3>
        <p className="mt-1 text-[13px] text-muted-foreground">{intl.formatMessage(messages.itineraryWindow)}</p>
      </div>
      <Clock3 className="h-5 w-5 text-primary" aria-hidden="true" />
    </div>
    <div className="mt-3 grid gap-2">
      {itinerary.map((item) => (
        <article key={item.day} className="rounded-[15px] border border-border bg-background/70 p-3.5">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[13px] font-bold text-primary">{intl.formatMessage(messages.day, { day: item.day })}</span>
            {item.summary ? <p className="text-[15px] font-semibold text-foreground">{item.summary}</p> : null}
          </div>
          {item.slots.length > 0 ? (
            <div className="mt-3 grid gap-2">
              {item.slots.map((slot) => (
                <div key={`${item.day}-${slot.time}-${slot.detail}`} className="grid grid-cols-[3.25rem_1fr] gap-2 text-[15px] leading-relaxed">
                  <span className="font-semibold tabular-nums text-primary">{slot.time}</span>
                  <span className="text-muted-foreground">{slot.detail}</span>
                </div>
              ))}
            </div>
          ) : null}
        </article>
      ))}
    </div>
    {remainingDays > 0 ? (
      <p className="mt-3 rounded-[15px] border border-dashed border-border bg-background/50 p-3.5 text-[15px] leading-relaxed text-muted-foreground">
        {intl.formatMessage(messages.restOfTrip, { days: remainingDays })}
      </p>
    ) : null}
  </section>
);


